'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceSelectorProps {
  onServiceChange: (service: ServiceSelection) => void;
}

interface ServiceSelection {
  serviceType: string;
  vehicleBrand: string;
  vehicleYear: string;
  vehicleModel: string;
}

const ServiceSelector = ({ onServiceChange }: ServiceSelectorProps) => {
  const [selection, setSelection] = useState<ServiceSelection>({
    serviceType: '',
    vehicleBrand: '',
    vehicleYear: '',
    vehicleModel: ''
  });

  const services = [
    'Oil Change',
    'Brake Service',
    'Tire Rotation',
    'Engine Diagnostics',
    'Air Filter Replacement',
    'Battery Service',
    'Body Shop',
  ];

  const brands = ['BMW', 'Lexus', 'Audi', 'Mercedes', 'Toyota', 'Honda', 'Acura'];
  
  const years = Array.from({ length: 15 }, (_, i) => (new Date().getFullYear() - i).toString());

  const models: Record<string, string[]> = {
    'BMW': ['3 Series', '5 Series', '7 Series', 'X3', 'X5', 'X7', 'M3', 'M5'],
    'Lexus': ['ES', 'IS', 'GS', 'LS', 'RX', 'NX', 'GX', 'LX'],
    'Audi': ['A3', 'A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8'],
    'Mercedes': ['C-Class', 'E-Class', 'S-Class', 'GLA', 'GLC', 'GLE', 'GLS'],
    'Toyota': ['Camry', 'Corolla', 'RAV4', 'Highlander', 'Prius', 'Tacoma', 'Tundra', 'Sienna'],
    'Honda': ['Civic', 'Accord', 'CR-V', 'Pilot', 'Odyssey', 'Fit', 'HR-V', 'Ridgeline'],
    'Acura': ['MDX', 'RDX', 'TLX', 'Integra', 'ILX', 'TSX', 'RLX', 'RSX']
  };

  const handleChange = (field: keyof ServiceSelection, value: string) => {
    const newSelection = { ...selection, [field]: value };
    if (field === 'vehicleBrand') {
      newSelection.vehicleModel = '';
    }
    setSelection(newSelection);
    onServiceChange(newSelection);
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-6">
      <div className="flex items-center space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-text-primary">
            Select Service & Vehicle
          </h2>
          <p className="font-body text-sm text-text-secondary">
            Choose your service and vehicle details for accurate pricing
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="serviceType" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Service Type
          </label>
          <div className="relative">
            <Icon 
              name="WrenchScrewdriverIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="serviceType"
              value={selection.serviceType}
              onChange={(e) => handleChange('serviceType', e.target.value)}
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <option value="">Select Service</option>
              {services.map(service => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="vehicleBrand" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Vehicle Brand
          </label>
          <div className="relative">
            <Icon 
              name="TruckIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="vehicleBrand"
              value={selection.vehicleBrand}
              onChange={(e) => handleChange('vehicleBrand', e.target.value)}
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <option value="">Select Brand</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="vehicleYear" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Vehicle Year
          </label>
          <div className="relative">
            <Icon 
              name="CalendarIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="vehicleYear"
              value={selection.vehicleYear}
              onChange={(e) => handleChange('vehicleYear', e.target.value)}
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              <option value="">Select Year</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="vehicleModel" className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider block">
            Vehicle Model
          </label>
          <div className="relative">
            <Icon 
              name="CpuChipIcon" 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
            <select
              id="vehicleModel"
              value={selection.vehicleModel}
              onChange={(e) => handleChange('vehicleModel', e.target.value)}
              disabled={!selection.vehicleBrand}
              className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select Model</option>
              {selection.vehicleBrand && models[selection.vehicleBrand]?.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selection.serviceType && selection.vehicleBrand && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-caption text-xs font-medium text-text-primary mb-1">
                Selection Complete
              </p>
              <p className="font-body text-xs text-text-secondary leading-relaxed">
                Showing pricing comparison for {selection.serviceType} on {selection.vehicleYear || 'your'} {selection.vehicleBrand} {selection.vehicleModel || 'vehicle'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;